import React, { useEffect, memo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  useCreateMutation,
  useUpdatePatchMutation,
  useDetailQuery,
} from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { handleFormErrors } from 'utils/formErrorHandler';

interface SharedCategoryFormValues {
  name: string;
  description: string;
  isActive: boolean;
}

const validationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .required('Name is required')
    .max(255, 'Name must be at most 255 characters'),
  description: Yup.string().trim().max(1000, 'Description must be at most 1000 characters'),
  isActive: Yup.boolean(),
});

interface SharedCategoryCreateOrUpdateProps {
  isOpen: boolean;
  toggle: () => void;
  categoryId?: string;
  onSuccess?: () => void;
}

const SharedCategoryCreateOrUpdate: React.FC<SharedCategoryCreateOrUpdateProps> = ({
  isOpen,
  toggle,
  categoryId,
  onSuccess,
}) => {
  const isEditMode = Boolean(categoryId);

  const { data: fetchedCategory, isLoading: loadingCategory } =
    useDetailQuery<SharedCategoryFormValues>(
      API_ENDPOINTS.SAAS.SHARED_CATEGORIES.GET_BY_ID(categoryId ?? ''),
      {
        id: categoryId ?? '',
      },
    );

  const { mutate: createCategory, isPending: creating } = useCreateMutation(
    API_ENDPOINTS.SAAS.SHARED_CATEGORIES.BASE,
  );

  const { mutate: updateCategory, isPending: updating } = useUpdatePatchMutation(
    `${API_ENDPOINTS.SAAS.SHARED_CATEGORIES.BASE}/${categoryId}`,
  );

  const initialValues: SharedCategoryFormValues = {
    name: '',
    description: '',
    isActive: true,
    ...fetchedCategory,
  };

  const formik = useFormik<SharedCategoryFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, formikHelpers: FormikHelpers<SharedCategoryFormValues>) => {
      const payload = {
        ...values,
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
      };

      const handleSuccess = () => {
        formik.resetForm();
        toggle();
        onSuccess?.();
      };

      const handleError = (error: any) => {
        handleFormErrors<SharedCategoryFormValues>(error, formikHelpers);
      };

      if (isEditMode) {
        updateCategory(
          { data: values, params: { id: categoryId } },
          { onSuccess: handleSuccess, onError: handleError },
        );
      } else {
        createCategory(payload, { onSuccess: handleSuccess, onError: handleError });
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        {isEditMode ? 'Update Category' : 'Create Category'}
      </ModalHeader>
      <form onSubmit={formik.handleSubmit} noValidate>
        <ModalBody>
          {isEditMode && loadingCategory ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              <FormGroup>
                <Label for="name">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Enter category name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.name && Boolean(formik.errors.name)}
                />
                {formik.touched.name && formik.errors.name && (
                  <small className="text-danger">{formik.errors.name}</small>
                )}
              </FormGroup>

              <FormGroup>
                <Label for="description">Description</Label>
                <Input
                  id="description"
                  name="description"
                  type="textarea"
                  placeholder="Enter description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.description && Boolean(formik.errors.description)}
                />
                {formik.touched.description && formik.errors.description && (
                  <small className="text-danger">{formik.errors.description}</small>
                )}
              </FormGroup>

              <FormGroup check>
                <Label check>
                  <Input
                    name="isActive"
                    type="checkbox"
                    checked={formik.values.isActive}
                    onChange={formik.handleChange}
                  />
                  Active
                </Label>
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={creating || updating || loadingCategory}>
            {creating || updating ? (
              <>
                <Spinner size="sm" color="light" /> {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : isEditMode ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default memo(SharedCategoryCreateOrUpdate);
